import LogTypes from "../../logs/exports.js";

export type LogSearchCompletionKind =
    | "field"
    | "function"
    | "operator"
    | "keyword"
    | "value"
    | "snippet"
    | "placeholder";

export type LogSearchCompletion = {
    label: string;
    apply?: string;
    detail: string;
    info: string;
    kind: LogSearchCompletionKind;
};

export const LogSearchFields = [
    { label: "id", detail: "number", info: "Identifiant interne du log." },
    { label: "type", detail: "string", info: "Type d'événement enregistré." },
    { label: "executor", detail: "snowflake", info: "Utilisateur ayant déclenché l'action." },
    { label: "target", detail: "snowflake", info: "Utilisateur ou objet ciblé." },
    { label: "channel", detail: "snowflake", info: "Salon principal lié au log." },
    { label: "from", detail: "snowflake", info: "Ancienne valeur ou ancien salon." },
    { label: "to", detail: "snowflake", info: "Nouvelle valeur ou nouveau salon." },
    { label: "role", detail: "snowflake", info: "Rôle principal lié au log." },
    { label: "createdAt", detail: "timestamp", info: "Timestamp du log en millisecondes." },
    { label: "extra.content", detail: "string", info: "Contenu textuel d'un message." },
    { label: "extra.content[0]", detail: "string", info: "Premier caractère du contenu." },
    { label: "extra.messageId", detail: "snowflake", info: "ID Discord du message." },
    { label: "extra.embeds", detail: "json[]", info: "Liste des embeds du message." },
    { label: "extra.embeds[0].description", detail: "string", info: "Description du premier embed." },
    { label: "extra.embeds[*].description", detail: "string[]", info: "Descriptions des embeds." },
    { label: "extra.embeds[*].title", detail: "string[]", info: "Titres des embeds." },
    { label: "extra.attachments[*].url", detail: "string[]", info: "URLs des pièces jointes." },
    { label: "extra.mentions.users[*]", detail: "snowflake[]", info: "IDs des utilisateurs mentionnés." },
    { label: "extra.mentions.roles[*]", detail: "snowflake[]", info: "IDs des rôles mentionnés." },
    { label: "extra.roles[*].id", detail: "snowflake[]", info: "IDs des rôles ajoutés/retirés." },
    { label: "extra.roles[*].name", detail: "string[]", info: "Noms des rôles ajoutés/retirés." },
] as const;

export const LogSearchOperators = [
    { label: "and", detail: "keyword", info: "Les deux conditions doivent être vraies." },
    { label: "or", detail: "keyword", info: "Au moins une condition doit être vraie." },
    { label: "not", detail: "keyword", info: "Inverse une condition." },
    { label: "in", detail: "operator", info: "La valeur est dans un ensemble." },
    { label: "not in", detail: "operator", info: "La valeur n'est pas dans un ensemble." },
    { label: "==", detail: "operator", info: "Égal à." },
    { label: "!=", detail: "operator", info: "Différent de." },
    { label: ">", detail: "operator", info: "Supérieur à." },
    { label: ">=", detail: "operator", info: "Supérieur ou égal à." },
    { label: "<", detail: "operator", info: "Inférieur à." },
    { label: "<=", detail: "operator", info: "Inférieur ou égal à." },
] as const;

export const LogSearchFunctions = [
    { label: "contains", apply: "contains($1, \"$2\")", detail: "function", info: "Vérifie qu'un texte contient une sous-chaîne." },
    { label: "match", apply: "match($1, \"$2\")", detail: "function", info: "Vérifie une expression régulière." },
    { label: "lower", apply: "lower($1)", detail: "function", info: "Convertit une valeur texte en minuscules." },
    { label: "exists", apply: "exists($1)", detail: "function", info: "Vérifie qu'une valeur existe." },
    { label: "length", apply: "length($1)", detail: "function", info: "Retourne la taille d'une chaîne ou d'un tableau." },
    { label: "is_bot", apply: "is_bot($1)", detail: "runtime", info: "Vérifie si un utilisateur est un bot." },
    { label: "roles", apply: "roles($1)[*].id", detail: "runtime", info: "Rôles actuels du membre." },
] as const;

export const LogSearchSnippets = [
    { label: "type equals", apply: "type == \"$1\"", detail: "snippet", info: "Filtrer par type de log." },
    { label: "type in", apply: "type in { \"$1\", \"$2\" }", detail: "snippet", info: "Filtrer plusieurs types." },
    { label: "message contains", apply: "contains(extra.content, \"$1\")", detail: "snippet", info: "Chercher dans le contenu des messages." },
    { label: "role added", apply: "type == \"member_role_add\" and extra.roles[*].id in { \"$1\" }", detail: "snippet", info: "Ajout d'un rôle précis." },
    { label: "mentioned user", apply: "extra.mentions.users[*] in { \"$1\" }", detail: "snippet", info: "Messages mentionnant un utilisateur." },
    { label: "not bot", apply: "not is_bot(executor)", detail: "snippet", info: "Exclure les actions de bots." },
] as const;

export const LogSearchPlaceholders = [
    { label: "@string", apply: "@string::\"Texte\"", detail: "placeholder", info: "Paramètre texte pour un bookmark." },
    { label: "@role", apply: "@role::\"Rôle\"", detail: "placeholder", info: "Paramètre rôle pour un bookmark." },
    { label: "@channel", apply: "@channel::\"Salon\"", detail: "placeholder", info: "Paramètre salon pour un bookmark." },
    { label: "@user", apply: "@user::\"Utilisateur\"", detail: "placeholder", info: "Paramètre utilisateur pour un bookmark." },
    { label: "@number", apply: "@number::\"Nombre\"", detail: "placeholder", info: "Paramètre numérique pour un bookmark." },
] as const;

export const LogSearchExamples = [
    {
        name        : "Messages modifiés ou supprimés",
        expression  : `type in { "message_delete", "message_edit" }`,
        description : "Retrouver les suppressions et éditions de messages.",
    },
    {
        name        : "Actions d'un modérateur",
        expression  : `executor == "123456789" and type == "member_ban"`,
        description : "Filtrer une action précise faite par un utilisateur.",
    },
    {
        name        : "Invitation Discord",
        expression  : `contains(extra.content, "discord.gg")`,
        description : "Chercher une chaîne dans le contenu des messages.",
    },
    {
        name        : "Messages supprimés hors bots",
        expression  : `type == "message_delete" and not is_bot(executor)`,
        description : "Exclure les actions effectuées par des bots.",
    },
    {
        name        : "Ajout d'un rôle précis",
        expression  : `type == "member_role_add" and extra.roles[*].id in { "123456789" }`,
        description : "Chercher les ajouts d'un rôle via les données historiques du log.",
    },
    {
        name        : "Mention utilisateur",
        expression  : `extra.mentions.users[*] in { "123456789" }`,
        description : "Chercher les messages mentionnant un utilisateur.",
    },
    {
        name        : "Rôle actuel de l'exécuteur",
        expression  : `roles(executor)[*].id in { "123456789" }`,
        description : "Chercher selon les rôles actuels d'un membre.",
    },
    {
        name        : "Messages avec embeds",
        expression  : `length(extra.embeds) > 0`,
        description : "Chercher les logs qui contiennent au moins un embed.",
    },
    {
        name        : "Premier embed contenant un texte",
        expression  : `contains(extra.embeds[0].description, "raid")`,
        description : "Chercher dans la description du premier embed.",
    },
];

export const LogSearchLogTypeCompletions = LogTypes.map((logType) => ({
    label: `"${logType.name}"`,
    apply: `"${logType.name}"`,
    detail: "log type",
    info: `Type de log: ${logType.name}`,
    kind: "value" as const,
}));

export const LogSearchFieldNames = LogSearchFields.map((field) => field.label);
export const LogSearchFunctionNames = LogSearchFunctions.map((fn) => fn.label);
export const LogSearchOperatorNames = LogSearchOperators.map((operator) => operator.label);
